/**
 * Реестры источников поиска для меншнов композера Activity.
 *
 * Два вида провайдеров:
 * - scope-mention (`#`) — поиск сущностей, привязываемых к сообщению как scope
 *   (в CRM это сделки партнёра; в другом модуле — например, подзадачи проекта);
 * - contact-search (inline-picker `/контакт`) — поиск контактных лиц,
 *   упоминание вставляется чипом (`comment-contact-mention` модуля comments).
 *
 * Провайдеры регистрируются модулями-владельцами (upsert по `id`), активный
 * выбирается первым подошедшим `isAvailable(ctx)`. Без провайдера соответствующий
 * меншн просто недоступен в треде (graceful fallback).
 */

/** Контекст треда, в котором открыт композер. */
export type ActivityMentionThreadContext = {
  threadEntityType?: string | null;
  threadEntityUuid?: string | null;
};

/** Элемент выдачи scope-меншна (`#`). */
export type ActivityScopeMentionItem = {
  uuid: string;
  /** Отображаемый label (например, номер сделки «66220»). */
  label: string;
  /** Доп. текст для локальной фильтрации. */
  searchText?: string;
  /** `repository_code` сущности — станет `entity_type` scope сообщения. */
  entity_type: string;
};

export type ActivityScopeMentionProvider = {
  id: string;
  isAvailable: (ctx: ActivityMentionThreadContext) => boolean;
  search: (query: string, ctx: ActivityMentionThreadContext) => Promise<ActivityScopeMentionItem[]>;
};

/** Элемент выдачи поиска контактных лиц. */
export type ActivityContactSearchItem = {
  uuid: string;
  label: string;
  searchText?: string;
};

export type ActivityContactSearchProvider = {
  id: string;
  isAvailable: (ctx: ActivityMentionThreadContext) => boolean;
  search: (query: string, ctx: ActivityMentionThreadContext) => Promise<ActivityContactSearchItem[]>;
};

class ActivityMentionRegistry {
  private static instance: ActivityMentionRegistry | null = null;

  private readonly scopeProvidersById = new Map<string, ActivityScopeMentionProvider>();
  private readonly contactProvidersById = new Map<string, ActivityContactSearchProvider>();

  static getInstance(): ActivityMentionRegistry {
    if (!ActivityMentionRegistry.instance) {
      ActivityMentionRegistry.instance = new ActivityMentionRegistry();
    }
    return ActivityMentionRegistry.instance;
  }

  registerScopeProvider(provider: ActivityScopeMentionProvider): void {
    this.scopeProvidersById.set(provider.id, provider);
  }

  registerContactProvider(provider: ActivityContactSearchProvider): void {
    this.contactProvidersById.set(provider.id, provider);
  }

  resolveScopeProvider(ctx: ActivityMentionThreadContext): ActivityScopeMentionProvider | null {
    for (const provider of this.scopeProvidersById.values()) {
      if (provider.isAvailable(ctx)) return provider;
    }
    return null;
  }

  resolveContactProvider(ctx: ActivityMentionThreadContext): ActivityContactSearchProvider | null {
    for (const provider of this.contactProvidersById.values()) {
      if (provider.isAvailable(ctx)) return provider;
    }
    return null;
  }
}

export const activityMentionRegistry = ActivityMentionRegistry.getInstance();

/** Регистрирует провайдер scope-меншна (`#`); upsert по `id`. */
export function registerActivityScopeMentionProvider(
  provider: ActivityScopeMentionProvider
): ActivityScopeMentionProvider {
  activityMentionRegistry.registerScopeProvider(provider);
  return provider;
}

/** Регистрирует провайдер поиска контактных лиц; upsert по `id`. */
export function registerActivityContactSearchProvider(
  provider: ActivityContactSearchProvider
): ActivityContactSearchProvider {
  activityMentionRegistry.registerContactProvider(provider);
  return provider;
}
